import React, { useEffect, useState, useRef } from "react";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import { formatEvents } from "../../helper/formatEvents";
import moment from "moment";
import "./CoParentCalendarNew.css";
import {
  Button,
  Segmented,
  Tag,
  Select,
  message,
  Dropdown,
  Radio,
  Space,
  Spin,
} from "antd";
import {
  PlusOutlined,
  SettingOutlined,
  LeftOutlined,
  RightOutlined,
  EditOutlined,
  DownOutlined,
} from "@ant-design/icons";
import { URL } from "../../helper/Mainurl";
import { scheduleData } from "./scheduleData";
import AddTask from "./AddCoParentTask";
import ViewCoParentTask from "./ViewCoParentTask";
import ViewTask from "../CalenderModal/ViewTask";
import ViewEvent from "../CalenderModal/ViewEvent";
import FilterModal from "../CalenderModal/FilterModal";
import filterIcon from "../../Assets/calender/action-filter-outline.png";
import EditCoparent from "./EditCoparent";
import { useSubscriptions } from "../../contexts/subscriptionsContext";
import { hasFreeTrialExpired } from "../../helper/hasFreeTrialExpired";
import { useUserInfo } from "../../contexts/userInfo";

function hexToRgba(hex, alpha) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

const dayToIndex = {
  Sunday: 0,
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
};
const { Option } = Select;

let lastClickTime = 0;
const CoParentCalendarNew = ({
  onSelect,
  coParentsMetaData,
  getCoparentMetaData,
}) => {
  const token = localStorage.getItem("authToken");
  const calendarRef = useRef();
  const [events, setEvents] = useState([]);
  const [couples, setCouples] = useState([]);
  const [preselectedDate, setPreselectedDate] = useState(null);

  const { purchasedSubscription } = useSubscriptions();
  const { userInfo } = useUserInfo();

  const [calendarView, setCalendarView] = useState("timeGridWeek");
  const [calendarApi, setCalendarApi] = useState(null);
  const [calendarTitle, setCalendarTitle] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [ViewTaskmodalOpen, setViewTaskmodalOpen] = useState(false);
  const [ViewEventmodalOpen, setViewEventmodalOpen] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState([]);
  const [isEditCoparentOpen, setIsEditCoparentOpen] = useState(false);
  const [childOptions, setChildOptions] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [uniqueParents, setUniqueParents] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedParentID, setSelectedParentID] = useState(null);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showViewTask, setShowViewTask] = useState(false);
  const [showViewEvent, setShowViewEvent] = useState(false);
  const [viewTaskData, setViewTaskData] = useState(null);
  const [viewEventData, setViewEventData] = useState(null);
  const [metaData, setMetaData] = useState([]);
  const [calenderMetaData, setCalenderMetaData] = useState([]);
  const [metadataCache, setMetadataCache] = useState(null);
  const [metadataLoading, setMetadataLoading] = useState(false);
  const [calenderData, setCalenderData] = useState(null);

  const hasCoParentFeature =
    purchasedSubscription?.services?.some(
      (service) =>
        service.name ===
        "Co-Parenting Calendar (shared child \narrangement tools)"
    ) || false;

  const isInTrial = !hasFreeTrialExpired(userInfo?.created_at);
  const currentUserId = userInfo?.user_id;

  const canEditCoParentSchedule =
    // Case 1: Subscribed plan with co-parenting feature
    hasCoParentFeature ||
    // Case 2: In active trial period
    isInTrial;

  useEffect(() => {
    const data = coParentsMetaData;
    if (!data) return;

    const {
      permissions,
      features,
      colors,
      schedule_types,
      couples,
      user_as_coparent_couples,
    } = data;

    setMetaData(data);
    setMetadataCache(data); // 👈 moved here

    // 🆕 All children for dropdown without duplicates
    const allChildrenSet = new Set();
    const allChildren = [];

    // 🆕 All unique parents
    const parentMap = new Map();

    (couples && couples.length > 0
      ? couples
      : user_as_coparent_couples
    ).forEach((couple) => {
      (couple.children || []).forEach((child) => {
        if (!allChildrenSet.has(child.id)) {
          allChildrenSet.add(child.id);
          allChildren.push({
            value: Number(child.id),
            label: child.name,
          });
        }
      });

      const { parent1, parent2 } = couple;

      if (parent1 && !parentMap.has(parent1.user_id)) {
        parentMap.set(parent1.user_id, parent1);
      }
      if (parent2 && !parentMap.has(parent2.user_id)) {
        parentMap.set(parent2.user_id, parent2);
      }
    });

    setCouples(couples.length ? couples : user_as_coparent_couples);
    setChildOptions(allChildren);
    setUniqueParents(Array.from(parentMap.values()));
  }, [coParentsMetaData]);

  const handleViewClose = () => {
    setShowViewTask(false);
    setViewTaskData(null);
  };

  useEffect(() => {
    // build initial events (if needed)
    const allEvents = [];
    scheduleData.forEach(({ parent1, parent2 }) => {
      allEvents.push(...generateTimeEvents(parent1));
      allEvents.push(...generateTimeEvents(parent2));
    });
    // setEvents(allEvents); // uncomment if you actually need it

    // if startDate & endDate exist (navigation / filter case), use them
    // otherwise default to current week (initial mount case)
    const monday =
      startDate || moment().startOf("week").add(1, "day").format("YYYY-MM-DD");
    const sunday = endDate || moment().day(7).format("YYYY-MM-DD");

    const params = buildParams(monday, sunday, selectedChild, selectedParentID);

    setIsEditCoparentOpen(false);
    resetHeaderLabels();
    colorHeaderBackground(params, "unified effect");
  }, [
    scheduleData,
    startDate,
    endDate,
    selectedChild,
    selectedParentID,
    hasInteracted,
  ]);

  const fetchAndSetEvents = async (start, end) => {
    setLoading(true);
    setEvents([]); // clear once at start of fetch
    try {
      const response = await fetch(
        `${URL}/v1/co-parenting/task-event-data?start_date=${start}&end_date=${end}`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response.json();
      if (data.code === 200) {
        const formattedEvents = formatEvents(data.data);
        setEvents(formattedEvents);
      }
    } catch (err) {
      message.error("Failed to load events");
    }
    setLoading(false);
  };

  // const handleViewChange = (value) => {
  //   let view = "";
  //   if (value === "Today") {
  //     view = "timeGridDay";
  //   } else if (value === "Week") {
  //     view = "timeGridWeek";
  //   } else if (value === "Month") {
  //     view = "dayGridMonth";
  //   }

  //   if (calendarApi) {
  //     calendarApi.changeView(view);
  //     setCalendarView(view);
  //   }
  // };

  const handleViewChange = (value) => {
    // setSetsegmentState(value);

    if (!calendarApi) return;

    if (value === "Today") {
      calendarApi.changeView("timeGridDay");
      calendarApi.today(); // Jump to today
      return;
    }

    if (value === "Week") {
      calendarApi.changeView("timeGridWeek");
      return; // datesSetHandler will update start/end + title + view
    }

    if (value === "Month") {
      calendarApi.changeView("dayGridMonth");
      return;
    }
  };

  const handlePrev = () => {
    if (calendarApi) {
      calendarApi.prev();
      updateDateRange();
    }
  };

  const handleNext = () => {
    if (calendarApi) {
      calendarApi.next();
      updateDateRange();
    }
  };
  // useEffect(() => {
  //   // if (!hasInteracted) return; // don’t fire on initial load

  //   const params = buildParams(
  //     startDate,
  //     endDate,
  //     selectedChild,
  //     selectedParentID
  //   );
  //   resetHeaderLabels();
  //   colorHeaderBackground(params);
  // }, [startDate, endDate, selectedChild, selectedParentID, hasInteracted]);

  const updateDateRange = () => {
    if (!calendarApi) return;
    const view = calendarApi.view;
    const start = formatDateLocal(view.currentStart);
    const end = formatDateLocal(view.currentEnd);
    setStartDate(start);
    setEndDate(end);
    setCalendarTitle(view.title);
  };

  const datesSetHandler = (info) => {
    setCalendarView(info.view.type);
    // Use the calendar's current view month, not info.start
    const currentMonth = moment(info.view.currentStart);
    const startOfWeek = moment(info.start).format("YYYY-MM-DD");
    const endOfWeek = moment(info.end).format("YYYY-MM-DD");
    // const startOfMonth = currentMonth
    //   .clone()
    //   .startOf("month")
    //   .format("YYYY-MM-DD");
    // const endOfMonth = currentMonth.clone().endOf("month").format("YYYY-MM-DD");
    setStartDate(startOfWeek);
    setEndDate(endOfWeek);
    fetchAndSetEvents(startOfWeek, endOfWeek);

    // const param = `?start_date=${startOfWeek}&end_date=${endOfWeek}`;
    // resetHeaderLabels();
    // colorHeaderBackground(param, 'datesSethandler');
    const formattedTitle = info.view.title.replace(
      /(\w+) (\d+), (\d+)/,
      "$2 $1, $3"
    );
    setCalendarTitle(formattedTitle);
  };

  const handleDataUpdate = () => {
    if (startDate && endDate) {
      fetchAndSetEvents(startDate, endDate);
    }
  };

  const formatDateLocal = (date) =>
    new Date(date).toLocaleDateString("en-CA", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });

  const generateTimeEvents = (parent) => {
    const events = [];
    parent?.pickup?.days?.forEach((day) =>
      events.push(
        createEvent(
          "Pickup",
          parent.name,
          day,
          parent.pickup.time,
          parent.color
        )
      )
    );
    parent?.dropoff?.days?.forEach((day) =>
      events.push(
        createEvent(
          "Dropoff",
          parent.name,
          day,
          parent.dropoff.time,
          parent.color
        )
      )
    );
    return events;
  };

  const createEvent = (type, name, day, time, color) => {
    const today = new Date();
    const targetIndex = dayToIndex[day];
    const offset = (targetIndex + 7 - today.getDay()) % 7;
    const eventDate = new Date();
    eventDate.setDate(today.getDate() + offset);
    const [hour, minute] = time.split(":");
    eventDate.setHours(parseInt(hour), parseInt(minute), 0, 0);
    return {
      title: `${type} - ${name}`,
      start: eventDate,
      allDay: false,
      backgroundColor: color,
      borderColor: color,
    };
  };
  const equalizeHeaderHeights = () => {
    const headerCells = document.querySelectorAll(".fc-col-header-cell");

    let maxHeight = 0;

    // Reset heights and find the tallest one
    headerCells.forEach((cell) => {
      cell.style.height = "auto"; // Reset first
      const height = cell.offsetHeight;
      if (height > maxHeight) maxHeight = height;
    });

    // Apply max height to all
    headerCells.forEach((cell) => {
      cell.style.height = `${maxHeight}px`;
    });
  };
  const resetHeaderLabels = () => {
    const headers = document.querySelectorAll(".fc-col-header-cell");
    headers.forEach((cell) => {
      const oldBadges = cell.querySelectorAll(".pickup-dropoff-label");
      oldBadges.forEach((el) => el.remove());
    });
  };

  // Call after calendar render
  const getColorById = (colorId, colors, parentType) => {
    if (colorId) {
      const colorObj = colors.find((color) => color.id == colorId);
      if (colorObj) return colorObj.value;
    }

    // Default fallback colors if API gives null
    return parentType === "parent1" ? "#FF0000" : "#9B5DE5";
  };
  const buildParams = (startDate, endDate, selectedChild, selectedParentID) => {
    let params = `?start_date=${startDate}&end_date=${endDate}`;

    if (calendarView === "dayGridMonth") {
      params += `&monthView=true`;
    }
    if (selectedChild) {
      params += `&child_ids[]=${selectedChild}`;
    }
    if (selectedParentID) {
      params += `&parent_ids[]=${selectedParentID}`;
    }
    return params;
  };

  const colorHeaderBackground = async (param, source = "unknown") => {
    try {
      const data = coParentsMetaData;
      if (!data) return;

      const res2 = await fetch(`${URL}/v1/co-parenting/calendar-data${param}`, {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const calJson = await res2.json();
      if (calJson.code !== 200) return;
      // console.log("calenderData", calenderData.data.calendar_data);
      setLoading(false);
      setCalenderData(calJson);
      const colors = data.colors;

      // Flatten all children
      const allChildren = (calJson.data.calendar_data || []).map((group) => ({
        parent1Nickname: group.parent1?.nickname || "Parent 1",
        parent2Nickname: group.parent2?.nickname || "Parent 2",
        parent1color_id: group.parent1?.color_id,
        parent2color_id: group.parent2?.color_id,
        children: group.children || [],
      }));
      const isMonthView = document.querySelector(".fc-daygrid-day");

      const headers = isMonthView
        ? document.querySelectorAll(".fc-daygrid-day")
        : document.querySelectorAll(".fc-col-header-cell");

      const formatTime = (timeStr) => {
        if (!timeStr) return "";
        const [hour, minute] = timeStr.split(":").map(Number);

        // return "HH:mm" if minutes are not zero, otherwise "HH"
        return minute !== 0
          ? `${String(hour).padStart(2, "0")}:${String(minute).padStart(
              2,
              "0"
            )}`
          : `${String(hour).padStart(2, "0")}`;
      };
      const dayMap = {
        Sun: "Sunday",
        Mon: "Monday",
        Tue: "Tuesday",
        Wed: "Wednesday",
        Thu: "Thursday",
        Fri: "Friday",
        Sat: "Saturday",
      };

      headers.forEach((cell) => {
        cell
          .querySelectorAll(".parent-header-wrapper")
          .forEach((el) => el.remove());
        const cellDate = new Date(cell.getAttribute("data-date"));
        let day;
        if (isMonthView) {
          // Derive weekday name directly
          if (cellDate) {
            day = cellDate.toLocaleDateString("en-US", { weekday: "long" });
          }
        } else {
          const shortDay = cell.textContent.trim().split(" ")[1];
          day = dayMap[shortDay];
        }

        if (!day) return;

        let parentResponsibilities = [];

        // Iterate over all children to find responsible_for for the current day
        allChildren.forEach(
          ({
            parent1Nickname,
            parent2Nickname,
            children,
            parent1color_id,
            parent2color_id,
            schedule_type_id,
          }) => {
            children.forEach((child) => {
              if (child.schedule_type_id == 42) {
                const parent1Days = child.parent1?.selected_days || [];
                const parent2Days = child.parent2?.selected_days || [];

                // ---- Parent1 ----
                if (parent1Days.includes(day)) {
                  const creationDate1 = child.parent1?.creation_date
                    ? new Date(child.parent1.creation_date)
                    : null;
                  if (!creationDate1 || cellDate >= creationDate1) {
                    if (child.parent1?.apply_same_time_all_days === "1") {
                      parentResponsibilities.push({
                        color: getColorById(parent1color_id, colors, "parent1"),
                        child_name: child.child_name,
                        parent1Nickname: parent1Nickname,
                        day,
                        text: `${
                          child.parent1.responsible_for === "drop_off"
                            ? parent2Nickname
                            : parent1Nickname
                        }: ${
                          child.parent1.responsible_for === "pick_up"
                            ? "Pickup"
                            : child.parent1.responsible_for === "drop_off"
                            ? "Dropoff"
                            : child.parent1.responsible_for
                        } at: ${formatTime(
                          child.parent1.pickup_time ||
                            child.parent1.dropoff_time
                        )}`,
                      });
                    } else {
                      child.parent1?.days?.forEach((d) => {
                        if (d.day === day) {
                          parentResponsibilities.push({
                            color: getColorById(
                              parent1color_id,
                              colors,
                              "parent1"
                            ),
                            child_name: child.child_name,
                            parent1Nickname: parent1Nickname,
                            day,
                            text: `${
                              d.responsible_for === "drop_off"
                                ? parent2Nickname
                                : parent1Nickname
                            }: ${
                              d.responsible_for === "pick_up"
                                ? "Pickup"
                                : d.responsible_for === "drop_off"
                                ? "Dropoff"
                                : d.responsible_for
                            } at: ${formatTime(
                              d.pickup_time || d.dropoff_time
                            )}`,
                          });
                        }
                      });
                    }
                  }
                }

                // ---- Parent2 ----
                if (parent2Days.includes(day)) {
                  const creationDate2 = child.parent2?.creation_date
                    ? new Date(child.parent2.creation_date)
                    : null;
                  if (!creationDate2 || cellDate >= creationDate2) {
                    if (child.parent2?.apply_same_time_all_days === "1") {
                      parentResponsibilities.push({
                        color: getColorById(parent2color_id, colors, "parent2"),
                        child_name: child.child_name,
                        parent2Nickname: parent2Nickname,
                        day,
                        text: `${
                          child.parent2.responsible_for === "drop_off"
                            ? parent1Nickname
                            : parent2Nickname
                        }: ${
                          child.parent2.responsible_for === "pick_up"
                            ? "Pickup"
                            : child.parent2.responsible_for === "drop_off"
                            ? "Dropoff"
                            : child.parent2.responsible_for
                        } at: ${formatTime(
                          child.parent2.pickup_time ||
                            child.parent2.dropoff_time
                        )}`,
                      });
                    } else {
                      child.parent2?.days?.forEach((d) => {
                        if (d.day === day) {
                          parentResponsibilities.push({
                            color: getColorById(
                              parent2color_id,
                              colors,
                              "parent2"
                            ),
                            child_name: child.child_name,
                            parent2Nickname: parent2Nickname,
                            day,
                            text: `${
                              d.responsible_for === "drop_off"
                                ? parent1Nickname
                                : parent2Nickname
                            }: ${
                              d.responsible_for === "pick_up"
                                ? "Pickup"
                                : d.responsible_for === "drop_off"
                                ? "Dropoff"
                                : d.responsible_for
                            } at: ${formatTime(
                              d.pickup_time || d.dropoff_time
                            )}`,
                          });
                        }
                      });
                    }
                  }
                }
              }

              if (child.schedule_type_id == 41) {
                if (isMonthView) {
                  const weekSchedules = child.week_schedules || [];

                  // Find which week number this cellDate belongs to relative to creation date
                  const creationDate = new Date(
                    weekSchedules[0]?.segments[0]?.creation_date
                  );
                  if (cellDate < creationDate) return;

                  const adjustedDay = (cellDate.getDay() + 6) % 7; // Mon=0..Sun=6

                  // Anchor: Monday of creation week
                  const creationAdjusted = (creationDate.getDay() + 6) % 7;
                  const firstMonday = new Date(creationDate);
                  firstMonday.setDate(
                    creationDate.getDate() - creationAdjusted
                  );

                  const diffDays = Math.floor(
                    (cellDate - firstMonday) / (1000 * 60 * 60 * 24)
                  );
                  const weekNumber = Math.floor(diffDays / 7) + 1;

                  // 2-2-3 → Repeat every 2 weeks (so week_number 1 & 2 are enough, then cycle)
                  const rotationIndex = (weekNumber - 1) % weekSchedules.length;
                  const activeWeek = weekSchedules[rotationIndex];

                  if (!activeWeek) return;

                  (activeWeek.segments || []).forEach((segment) => {
                    if (!segment.selected_days.includes(day)) return;

                    parentResponsibilities.push({
                      color:
                        segment.assigned_to === "parent1"
                          ? getColorById(parent1color_id, colors, "parent1")
                          : getColorById(parent2color_id, colors, "parent2"),
                      child_name: child.child_name,
                      parentNickname:
                        segment.assigned_to === "parent1"
                          ? parent1Nickname
                          : parent2Nickname,
                      day,
                      text: `${
                        segment.responsible_person === "parent1"
                          ? parent1Nickname
                          : parent2Nickname
                      }: ${
                        segment.responsible_for === "pick_up"
                          ? "Pickup"
                          : segment.responsible_for === "drop_off"
                          ? "Dropoff"
                          : segment.responsible_for
                      } at: ${formatTime(
                        segment.pickup_time || segment.dropoff_time
                      )}`,
                    });
                  });
                } else {
                  // ✅ Your existing weekView logic works fine
                  const weekSchedules = child.week_schedules || [];
                  weekSchedules.forEach((week) => {
                    (week.segments || []).forEach((segment) => {
                      const creationDate = segment.creation_date
                        ? new Date(segment.creation_date)
                        : null;
                      if (creationDate && cellDate < creationDate) return;

                      if (segment.selected_days.includes(day)) {
                        parentResponsibilities.push({
                          color:
                            segment.assigned_to === "parent1"
                              ? getColorById(parent1color_id, colors, "parent1")
                              : getColorById(
                                  parent2color_id,
                                  colors,
                                  "parent2"
                                ),
                          child_name: child.child_name,
                          parentNickname:
                            segment.assigned_to === "parent1"
                              ? parent1Nickname
                              : parent2Nickname,
                          day,
                          text: `${
                            segment.responsible_person === "parent1"
                              ? parent1Nickname
                              : parent2Nickname
                          }: ${
                            segment.responsible_for === "pick_up"
                              ? "Pickup"
                              : segment.responsible_for === "drop_off"
                              ? "Dropoff"
                              : segment.responsible_for
                          } at: ${formatTime(
                            segment.pickup_time || segment.dropoff_time
                          )}`,
                        });
                      }
                    });
                  });
                }
              }

              if (child.schedule_type_id == 40) {
                if (isMonthView) {
                  child.weeks.forEach((week) => {
                    Object.entries(week).forEach(([weekKey, weekData]) => {
                      if (!weekData) return;

                      const creationDate = new Date(weekData.creation_date);
                      if (cellDate < creationDate) return;

                      // Find which week of year this cellDate falls in
                      const adjustedDay = (cellDate.getDay() + 6) % 7;

                      // Anchor to creationDate (start of schedule)
                      const creationAdjusted = (creationDate.getDay() + 6) % 7;
                      const firstMonday = new Date(creationDate);
                      firstMonday.setDate(
                        creationDate.getDate() - creationAdjusted
                      );

                      // Days difference since schedule start
                      const diffDays = Math.floor(
                        (cellDate - firstMonday) / (1000 * 60 * 60 * 24)
                      );

                      // Week number relative to creationDate
                      const weekNumber = Math.floor(diffDays / 7) + 1;

                      // Alternate: odd weeks → week_number1, even weeks → week_number2
                      if (
                        (weekKey === "week_number1" && weekNumber % 2 === 1) ||
                        (weekKey === "week_number2" && weekNumber % 2 === 0)
                      ) {
                        parentResponsibilities.push({
                          color:
                            weekData.assigned_to === "parent1"
                              ? getColorById(parent1color_id, colors, "parent1")
                              : getColorById(
                                  parent2color_id,
                                  colors,
                                  "parent2"
                                ),
                          child_name: child.child_name,
                          parentNickname:
                            weekData.assigned_to === "parent1"
                              ? parent1Nickname
                              : parent2Nickname,
                          day,
                          text: `${
                            weekData.responsible_for === "drop_off"
                              ? weekData.assigned_to === "parent1"
                                ? parent2Nickname
                                : parent1Nickname
                              : weekData.assigned_to === "parent1"
                              ? parent1Nickname
                              : parent2Nickname
                          }: ${
                            weekData.responsible_for === "pick_up"
                              ? "Pickup"
                              : weekData.responsible_for === "drop_off"
                              ? "Dropoff"
                              : weekData.responsible_for
                          } at: ${formatTime(
                            weekData.pickup_time || weekData.dropoff_time
                          )}`,
                        });
                      }
                    });
                  });
                } else {
                  const weekData =
                    child.weeks[0].week_number1 || child.weeks[0].week_number2;
                  if (!weekData) return;

                  const creationDate = new Date(weekData.creation_date);
                  if (cellDate < creationDate) return; // ✅ Skip before creation_date
                  if (weekData.assigned_to === "parent1") {
                    parentResponsibilities.push({
                      color: getColorById(parent1color_id, colors, "parent1"),
                      child_name: child.child_name,
                      parentNickname: parent1Nickname,
                      day,
                      text: `${
                        weekData.responsible_for === "drop_off"
                          ? parent2Nickname
                          : parent1Nickname
                      }: ${
                        weekData.responsible_for == "pick_up"
                          ? "Pickup"
                          : weekData.responsible_for == "drop_off"
                          ? "Dropoff"
                          : weekData.responsible_for
                      } at: ${formatTime(
                        weekData.pickup_time || weekData.dropoff_time
                      )}`,
                    });
                  }

                  if (weekData.assigned_to === "parent2") {
                    parentResponsibilities.push({
                      color: getColorById(parent2color_id, colors, "parent2"),
                      child_name: child.child_name,
                      parentNickname: parent2Nickname,
                      day,
                      text: `${
                        weekData.responsible_for === "drop_off"
                          ? parent1Nickname
                          : parent2Nickname
                      }: ${
                        weekData.responsible_for == "pick_up"
                          ? "Pickup"
                          : weekData.responsible_for == "drop_off"
                          ? "Dropoff"
                          : weekData.responsible_for
                      } at: ${formatTime(
                        weekData.pickup_time || weekData.dropoff_time
                      )}`,
                    });
                  }
                }
              }
            });
          }
        );
        // Clear old elements

        // const oldWrapper = cell.querySelector(".parent-header-wrapper");
        //         if (oldWrapper) oldWrapper.remove();
        const oldBar = cell.querySelector(".top-color-bar");
        if (oldBar) oldBar.remove();

        const oldBadges = cell.querySelectorAll(".pickup-dropoff-label2");
        oldBadges.forEach((el) => el.remove());

        const removeDuplicates = Array.from(new Set(parentResponsibilities));
        // Add responsible_for labels

        if (isMonthView) {
          const content = cell.querySelector(".fc-daygrid-day-frame");
          if (content) {
            const labelWrapper = document.createElement("div");
            labelWrapper.className = "parent-header-wrapper";
            // labelWrapper.style.marginTop = "2px";

            parentResponsibilities.forEach(({ color, text, child_name }) => {
              const label = document.createElement("div");
              label.className = "pickup-dropoff-label";
              label.innerHTML = text;
              label.style.background = color;
              label.style.color = "white";
              label.style.fontSize = "10px";
              // label.style.padding = "2px 6px";
              // label.style.marginBottom = "2px";
              // label.style.borderRadius = "3px";
              label.style.textAlign = "left";
              label.setAttribute("child-tooltip", child_name);
              labelWrapper.appendChild(label);
            });

            content.appendChild(labelWrapper);
          }
        } else {
          const labelWrapper = document.createElement("div");
          labelWrapper.style.display = "flex";
          labelWrapper.style.justifyContent = "center";
          labelWrapper.style.flexWrap = "wrap";
          labelWrapper.style.marginBottom = "2px";
          labelWrapper.className = "parent-header-wrapper";
          removeDuplicates.forEach(({ color, text, child_name }) => {
            const label = document.createElement("div");
            label.className = "pickup-dropoff-label";
            label.innerHTML = text;
            label.style.background = color;
            label.style.color = "white";
            label.style.fontSize = "10px";
            label.style.padding = "2px 6px";
            label.style.borderRadius = "0px";
            label.style.width = "100%";
            label.style.textAlign = "left";
            label.style.whiteSpace = "nowrap";
            label.setAttribute("child-tooltip", child_name);
            labelWrapper.appendChild(label);
          });

          cell.prepend(labelWrapper);
        }
      });
    } catch (error) {
      console.error(error);
    }
  };
  function getColorLabelById(id) {
    // console.log("metaData", metaData)
    if (id) {
      const color = metaData?.colors?.find((color) => color.id === id);
      return color;
    } else {
      return { value: "#f5f5f5", label: "Default Color" }; // Default color if not found
    }
  }
  const handleTaskClick = (info) => {
    console.log("info", info.event.extendedProps);
    const eventData = {
      ...info.event.extendedProps,
      id: info.event.extendedProps.id,
      title: info.event.title,
      location: info.event.extendedProps.location,
      description: info.event.extendedProps.description,
      start: info.event.start,
      start_time: info.event.start_time,
      end_time: info.event.end_time,
      start_date: moment(info.event.start).format("YYYY-MM-DD"),
      end_date: moment(info.event.end).format("YYYY-MM-DD"),
      occurrenceDate: moment(info.event.start).format("YYYY-MM-DD"),
      end: info.event.end_time,
      displaynameId: info.event.extendedProps.display_name_id,
      reminder: info.event.extendedProps.reminder,
      createdBy: info.event.extendedProps.created_by,
      updatedBy: info.event.extendedProps.updatedBy,
      type: info.event.extendedProps.type,
      priorityId: info.event.extendedProps.priority_id,
      progressStatusId: info.event.extendedProps.progress_status_id,
      progress_status_id: info.event.extendedProps.progress_status_id,
      display_name_id: info.event.extendedProps.display_name_id,
      priority_id: info.event.extendedProps.priority_id,
      assigned_by: info.event.extendedProps.assigned_by,
      checklists: info.event.extendedProps.checklists || [],
      color: info.event.backgroundColor,
      assignees: info.event.extendedProps.assignees || [],
      collaborators: info.event.extendedProps.collaborators,
      repeat_option: info.event.extendedProps.repeat_option,
      repetition_end_date: info.event.extendedProps.repetition_end_date,
      exclude_saturdays: info.event.extendedProps?.exclude_saturdays,
      exclude_sundays: info.event.extendedProps?.exclude_sundays,
      include_weekends: info.event.extendedProps?.include_weekends,
      included_weekend_days: info.event.extendedProps?.included_weekend_days,
      primary_user_id: info.event.extendedProps.primary_user_id,
      combination_id: info.event.extendedProps.combination_id,
    };
    setViewTaskData(eventData);
    setShowViewTask(true);
  };
  const mergeParentResponsibilities = (parentResponsibilities) => {
    const merged = [];

    parentResponsibilities.forEach((item) => {
      // Use day + text as the grouping key
      const key = `${item.day}|${item.text}`;
      const existing = merged.find(
        (m) => m.day === item.day && m.text === item.text
      );
      if (existing) {
        // Concatenate child_name
        existing.child_name += `, ${item.child_name}`;
      } else {
        // Add a copy of the item
        merged.push({ ...item });
      }
    });

    return merged;
  };

  const getCalenderMetaData = async () => {
    try {
      const response = await fetch(`${URL}/v1/calendar/get-calendar-metadata`, {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (data.code == 200) {
        // console.log("getCalenderMetaData", data.data);
        setCalenderMetaData(data.data);
      } else {
        console.error("Invalid response from server:", data); // Log detailed error
        // message.error("Failed to load profile data."); // User-friendly error message
      }
      // console.log("response>>>>>", data);
    } catch (error) {}
  };
  const handleTagClick = (parentID) => {
    console.log(parentID, "parentID");

    // Decide role: parent1 or parent2
    const parentRole =
      parentID.user_id === parentID.primary_user_id ? "parent1" : "parent2";
    console.log("Clicked role:", parentRole);

    setSelectedParentID((prev) => {
      if (prev === parentRole) {
        return null;
      } else {
        return parentRole;
      }
    });

    setHasInteracted(true);
    console.log("Clicked Parent:", parentID);
  };
  useEffect(() => {
    if (token) {
      // ✅ Optional: only call if token is available
      // getCoparentChild();
      getCalenderMetaData();
    }
  }, []);
  const handleSelect = () => {
    if (typeof onSelect === "function") {
      onSelect(selectedChild);
      console.log("Selected value New:", selectedChild);
    } else {
      console.error("onSelect is not a function");
    }
    setOpen(false);
  };
  const handleReset = () => {
    setSelectedChild(null); // clear filter

    // 🔹 Call API without child filter
    const params = buildParams(startDate, endDate, null, selectedParentID);
    resetHeaderLabels();
    colorHeaderBackground(params);

    setOpen(false);
  };

  const dropdownContent = (
    <div
      style={{
        padding: "16px",
        minWidth: "200px",
        backgroundColor: "#ffffff",
        WebkitBoxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.25)",
        borderBottomRightRadius: "10px",
        borderBottomLeftRadius: "10px",
      }}
    >
      {loading ? (
        <Spin />
      ) : (
        <>
          <Radio.Group
            onChange={(e) => setSelectedChild(e.target.value)}
            value={selectedChild}
            style={{ display: "flex", flexDirection: "column", gap: "12px" }}
          >
            {childOptions.map((child) => (
              <Radio
                key={child.value}
                value={child.value}
                className="radioSelect"
              >
                {child.label}
              </Radio>
            ))}
          </Radio.Group>

          <Button
            type="primary"
            block
            style={{
              marginTop: 16,
              backgroundColor: "#9B5DE5",
              borderColor: "#9B5DE5",
            }}
            onClick={handleSelect}
          >
            Select
          </Button>

          <Button
            block
            style={{
              marginTop: 8,
            }}
            onClick={handleReset}
          >
            Reset
          </Button>
        </>
      )}
    </div>
  );
  const getCalendarDate = async () => {
    try {
      const response = await fetch(`${URL}/v1/co-parenting/get-setup`, {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (data.code == 200) {
        console.log("setup ID", data.data.setup_id);
      } else {
        console.error("Invalid response from server:", data); // Log detailed error
        // message.error("Failed to load profile data."); // User-friendly error message
      }
      console.log("response>>>>>", data);
    } catch (error) {}
  };

  const applyFilters = async (priorityId, progressId, type, colorId) => {
    let url = `${URL}/v1/co-parenting/task-event-data`;
    let param = `?priority_id[]=${priorityId}&progress_status_id[]=${progressId}&child_id=${selectedChild}&parent_id=${selectedParentID}&start_date=${startDate}&end_date=${endDate}&type=${type}&color_id=${colorId}`;
    const finalUrl = url + param;
    console.log("finalUrl", finalUrl);
    setLoading(true);
    try {
      const res = await fetch(finalUrl, {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "Cache-Control": "no-cache",
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json();
      setLoading(false);
      if (data.code === 200) {
        const formattedEvents = formatEvents(data.data);
        // console.log("formattedEvents>>", formattedEvents)
        setEvents(formattedEvents);
      }
      console.log(data);
      setIsFilterModalOpen(false);
    } catch (error) {
      setLoading(false);
      console.error(error.message);
    }
  };

  const isPrimaryUser =
    coParentsMetaData?.couples?.length > 0 &&
    !coParentsMetaData?.user_as_coparent_couples?.length;

  const isCoParentUser =
    coParentsMetaData?.user_as_coparent_couples?.length > 0 &&
    !coParentsMetaData?.couples?.length;

  const isSetupNotDone =
    coParentsMetaData?.couples?.length === 0 &&
    coParentsMetaData?.user_as_coparent_couples?.length === 0;

  const openAddModal = (date = null) => {
    setPreselectedDate(date);
    setIsModalOpen(true);
  };

  const closeAddModal = () => {
    setIsModalOpen(false);
    setPreselectedDate(null);
  };

  const handleDateDoubleClick = (info) => {
    const now = new Date().getTime();
    const diff = now - lastClickTime;

    if (diff < 400) {
      // ✅ Double-click detected
      const clickedDate = moment(info.date).format("D MMMM YYYY");
      openAddModal(clickedDate); // <-- call your existing modal function
    }

    lastClickTime = now;
  };
  return (
    <Spin spinning={loading} tip="Loading...">
      <div>
        <div className="calendar-container">
          <div className="co-parent-calendar-wrapper">
            <div className="button-container">
              <Button
                className="white-bg"
                type="primary"
                icon={
                  <img
                    src={filterIcon}
                    alt="filter"
                    style={{ width: 16, height: 16 }}
                  />
                }
                onClick={() => setIsFilterModalOpen(true)}
              >
                Filter
              </Button>
            </div>
            <div className="calendar-header">
              <h2>Co-Parent Calendar</h2>
              <Segmented
                options={["Today", "Week", "Month"]}
                defaultValue="Week"
                onChange={handleViewChange}
                className="custom-segmented"
              />
              <div className="calendar-actions">
                <Button
                  icon={<PlusOutlined />}
                  onClick={() => openAddModal()}
                  style={{
                    display:
                      !calenderData || !coParentsMetaData
                        ? "none" // hide while loading
                        : coParentsMetaData?.user_as_coparent_couples?.some(
                            (couple) =>
                              couple?.parent2?.permission_id === "4" && // read-only access
                              couple?.parent2?.coparent_id === currentUserId && // logged-in user is coparent
                              couple?.parent1?.primary_user_id !== currentUserId // not the primary parent
                          )
                        ? "none"
                        : "",
                  }}
                >
                  Add Task
                </Button>
                {/* <SettingOutlined style={{ paddingLeft: 20 }} /> */}
              </div>
            </div>
          </div>

          <div className="filter-and-buttons">
            <div className="coParentFilterLeft">
              <div>
                <div>
                  {uniqueParents.map((parent) => {
                    let usedParent =
                      parent.user_id === parent.primary_user_id
                        ? "parent1"
                        : "parent2";
                    const color =
                      parent.color?.value ||
                      (usedParent === "parent1" ? "#FF0000" : "#9A4FE0"); // fallback color#9A4FE0
                    return (
                      <Tag
                        key={parent.user_id}
                        onClick={() => handleTagClick(parent)}
                        style={{ cursor: "pointer" }}
                        className={`tagParent ${
                          hasInteracted &&
                          selectedParentID ===
                            (parent.user_id === parent.primary_user_id
                              ? "parent1"
                              : "parent2")
                            ? "active"
                            : ""
                        }`}
                      >
                        {parent.name || "Parent"}
                        <span>
                          <div
                            className="colorParent"
                            style={{ backgroundColor: color }}
                          ></div>
                        </span>
                      </Tag>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="coParentFilterRight">
              {canEditCoParentSchedule && (isPrimaryUser || isCoParentUser) && (
                <Button
                  className="coparentEditBtn"
                  onClick={() => setIsEditCoparentOpen(true)}
                  style={{ padding: "10px 20px" }}
                  // disabled={
                  //   loading ||
                  //   !calenderData ||
                  //   calenderData?.message === "No parenting schedule data found."
                  // }
                >
                  Edit Parenting Schedule <EditOutlined />
                </Button>
              )}
              <Dropdown
                open={open}
                onOpenChange={(flag) => setOpen(flag)}
                dropdownRender={() =>
                  !calenderData ||
                  calenderData?.message ===
                    "No parenting schedule data found." ? (
                    <div style={{ padding: 8, color: "#999" }}>
                      "No child data found."
                    </div>
                  ) : (
                    dropdownContent
                  )
                }
                trigger={["click"]}
                className="coParentFilterChild"
              >
                <Button loading={loading} className="coParentFilterChildBtn">
                  {selectedChild ? (
                    <>
                      {`View Parenting Schedule for `}
                      <span className="filterChild">
                        {
                          childOptions.find(
                            (child) => child.value === selectedChild
                          )?.label
                        }
                      </span>
                    </>
                  ) : (
                    <span>View Parenting Schedule for</span>
                  )}
                  <DownOutlined style={{ marginLeft: 8 }} />
                </Button>
              </Dropdown>
            </div>
          </div>

          <div
            className="calenderhead2"
            style={{
              display: "flex",
              justifyContent: "space-between",
              padding: 10,
              borderBottom: "1px solid #ddd",
            }}
          >
            <div className="calenderheadlft" style={{ fontWeight: "bold" }}>
              {calendarTitle}
            </div>
            <div className="calenderRgt">
              <Button
                className="calenderNext"
                shape="circle"
                icon={<LeftOutlined />}
                onClick={handlePrev}
              />
              <Button
                className="calenderPrev"
                shape="circle"
                icon={<RightOutlined />}
                onClick={handleNext}
              />
            </div>
          </div>

          <div
            className={`calendar-scroll co-parent-calendar ${
              calendarView === "timeGridWeek" ? "week-scroll" : ""
            } ${calendarView === "timeGridDay" ? "day-scroll" : ""}`}
          >
            <FullCalendar
              height="auto"
              contentHeight="auto"
              expandRows={calendarView === "dayGridMonth"}
              aspectRatio={1.2}
              timeZone="UTC"
              key={calendarView} // Ensures re-render when view changes
              ref={(el) => el && setCalendarApi(el.getApi())}
              plugins={[timeGridPlugin, dayGridPlugin, interactionPlugin]}
              initialView={calendarView}
              firstDay={1}
              slotMinTime="06:00:00"
              slotMaxTime="24:00:00"
              headerToolbar={false}
              allDaySlot={false}
              dateClick={(info) => handleDateDoubleClick(info)}
              slotLabelFormat={{
                hour: "numeric",
                minute: "2-digit",
                // meridiem: "",
                hourCycle: "h23",
              }}
              dayHeaderFormat={{
                weekday: "short", // Sun, Mon, Tue
                day:
                  calendarView === "timeGridWeek" ||
                  calendarView === "timeGridDay"
                    ? "numeric"
                    : undefined,
              }}
              // height="auto"
              datesSet={(info) => {
                datesSetHandler(info);
                updateDateRange();
                setTimeout(equalizeHeaderHeights, 100);
              }}
              eventClick={(eventInfo) => {
                if (eventInfo.event.extendedProps.type === "task") {
                  // console.log("eventInfoeventInfoooo", eventInfo.event)
                  handleTaskClick(eventInfo);
                } else {
                  // console.log("eventInfo__handleEventClick",eventInfo)
                  handleTaskClick(eventInfo);
                }
              }}
              eventContent={(eventInfo) => {
                // Format time without the date
                const formattedStart = eventInfo.event.start
                  ? moment.utc(eventInfo.event.start).format("HH:mm")
                  : "";
                const formattedEnd = eventInfo.event.end
                  ? moment.utc(eventInfo.event.end).format("HH:mm")
                  : "";
                const fullTitle = eventInfo.event.title;
                // calendarView  = > "Today" "Month" "Week"
                let limitedTitle = fullTitle;

                if (calendarView === "Week") {
                  limitedTitle =
                    fullTitle.length > 25
                      ? fullTitle.slice(0, 25) + "..."
                      : fullTitle;
                } else if (calendarView === "Month") {
                  limitedTitle =
                    fullTitle.length > 20
                      ? fullTitle.slice(0, 20) + "..."
                      : fullTitle;
                }
                // console.log("eventInfoeventInfoeventInfo", eventInfo.event.extendedProps.color_id)
                return (
                  // <div style={{ backgroundColor: getColorLabelById(eventInfo.event.extendedProps.color_id)?.value || "#f5f5f5" }}>
                  <div>
                    <div>
                      <p className="calandarEventContent">
                        <p className="event-title">
                          <span className="event-time">
                            {formattedStart} - {formattedEnd}
                          </span>{" "}
                          <span
                            className="title-event"
                            style={{ fontWeight: "500" }}
                          >
                            {limitedTitle}
                          </span>
                        </p>
                      </p>
                    </div>
                  </div>
                );
              }}
              events={events.map((event) => ({
                ...event,
                backgroundColor: event.type == "task" ? "#F0F7FF" : "#FFEEEF", // ✅ Apply color directly
                borderColor: event.type == "task" ? "#F0F7FF" : "#FFEEEF", // Darker shade for border
              }))}
              eventDidMount={(info) => {
                const hexColor =
                  getColorLabelById(info.event.extendedProps.color_id)?.value ||
                  "#f5f5f5";

                const rgbaColor = hexToRgba(hexColor, 0.3); // Convert to rgba with 30% opacity

                info.el.style.setProperty(
                  "background-color",
                  rgbaColor,
                  "important"
                );
                info.el.style.setProperty(
                  "border-color",
                  rgbaColor,
                  "important"
                );
                info.el.style.setProperty("color", "#1D2433", "important");
              }}
              dayMaxEvents={2}
            />
          </div>
        </div>

        {isModalOpen && (
          <AddTask
            isModalOpen={isModalOpen}
            couples={couples}
            allChildren={childOptions}
            closeModal={closeAddModal}
            onTaskUpdate={handleDataUpdate}
            defaultDate={preselectedDate}
          />
        )}
        {viewTaskData && (
          <ViewCoParentTask
            isModalOpen={showViewTask}
            metaData={metaData}
            calenderMetaData={calenderMetaData}
            couples={couples}
            eventData={viewTaskData}
            allChildren={childOptions}
            closeModal={handleViewClose}
            onTaskUpdate={handleDataUpdate}
            calendarData={calenderData}
            coParentsMetaData={coParentsMetaData}
          />
        )}
        <FilterModal
          isFilterModalOpen={isFilterModalOpen}
          closeFilterModal={() => setIsFilterModalOpen(false)}
          startDate={startDate}
          endDate={endDate}
          applyFilters={applyFilters}
          calendarMetaData={calenderMetaData}
          getCalenderData={fetchAndSetEvents}
        />
        {ViewTaskmodalOpen && (
          <ViewTask
            isOpen={ViewTaskmodalOpen}
            onCancel={() => setViewTaskmodalOpen(false)}
            eventData={selectedEvent}
          />
        )}
        {ViewEventmodalOpen && (
          <ViewEvent
            isOpen={ViewEventmodalOpen}
            onCancel={() => setViewEventmodalOpen(false)}
            eventData={selectedEvent}
          />
        )}
        {isEditCoparentOpen && (
          <EditCoparent
            isOpen={isEditCoparentOpen}
            onCancel={() => setIsEditCoparentOpen(false)}
            couples={couples}
            alreadySet={coParentsMetaData && coParentsMetaData?.couples.length}
            colorHeaderBackground={colorHeaderBackground}
            startDate={startDate}
            endDate={endDate}
            getCoparentsMetaData={getCoparentMetaData}
            fetchAndSetEvents={fetchAndSetEvents}
          />
        )}
      </div>
    </Spin>
  );
};

export default CoParentCalendarNew;
